import type { MessageTextAreaRef } from '@halo/ui-chat-react';
import {
  MessageComposer,
  MessageTextArea,
  ToolBar,
  SendButton,
  useEditorContext,
  toaster,
  serializePayload,
} from '@halo/ui-chat-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { MsgPayloadType, isMyMessage, StateEnum, StickerCategory } from '@halo/chat-sdk';
import type { SerializePayloadResult } from '@halo/ui-chat-react';
import { useAudio, usePickFiles } from '@halo/hooks';
import { Flex } from '@chakra-ui/react';
import { useEffect } from 'react';
import type { SessionStore } from '@/store/client';
import { useAppStore } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import { getAccept, PickFileDialogSource } from '@/components/PickFileDialog';
import { useWalletStore } from '@/wallet';
import type { MsgReplyPreviewChangeEvent } from '../msg-parts';
import { MsgReplyPreview } from '../msg-parts';
import { useSessionContentsContext } from './session-contents/session-contents-context';

export interface SessionComposerProps {
  sessionStore: SessionStore;
  loading?: boolean;
  visible?: boolean;
  disabled?: boolean;
  onSend?: () => void;
  onPickFile?: (source: PickFileDialogSource, files: File[]) => void;
  // TODO amo: remove those props
  isGroup?: boolean;
  isMe?: boolean;
}

export function SessionComposer({
  sessionStore,
  loading,
  visible,
  disabled,
  onSend,
  onPickFile,
  isGroup = false,
  isMe = false,
}: SessionComposerProps) {
  const { t } = useTranslation();
  const refMessageTextArea = useRef<MessageTextAreaRef>(null);
  const { client, user, updateSession } = useAppStore(
    useShallowEnhance(['client', 'user', 'updateSession'])
  );
  const { canMention } = useSessionContentsContext();

  const { setLuckyDialogIsOpen, setTransferToIsOpen, setTransferDialogIsOpen } = useActionsStore(
    useShallowEnhance(['setLuckyDialogIsOpen', 'setTransferToIsOpen', 'setTransferDialogIsOpen'])
  );
  const readonly = useWalletStore((store) => store.readonly);
  const [valid, setValid] = useState(false);
  const { play: playSendSound } = useAudio({ src: '/media/send.mp3' });
  const focusTrigger = useMemo(() => visible && {}, [visible]);

  const [{ isEditing, editChanged }, setMsgReplyPreviewChangeEvent] =
    useState<MsgReplyPreviewChangeEvent>({
      isEditing: false,
      editChanged: false,
    });

  const { pick } = usePickFiles();

  const onDurationChange = useCallback(
    async (duration: number) => {
      const chat_id = sessionStore!.session!.chat_id!;
      await client?.Api.session.setSessionExpireDuration(chat_id, BigInt(duration));
      updateSession({
        chat_id,
        expire_duration: duration,
      });
    },
    [client?.Api.session, sessionStore, updateSession]
  );

  const refSessionStore = useRef(sessionStore);
  refSessionStore.current = sessionStore;

  const handleLuckyChange = useCallback(
    (type: string) => {
      const { isChangedPassword, readonly } = useWalletStore.getState();
      if (!readonly && !isChangedPassword) {
        toaster.warning({
          title: t('tips.pleaseSetYourWalletPasswordFirst'),
        });
        return;
      }
      switch (type) {
        case 'LuckyBox':
          setLuckyDialogIsOpen(true);
          break;
        case 'Transfer':
          if (isGroup) {
            setTransferToIsOpen(true);
          } else {
            setTransferDialogIsOpen(true);
          }
          break;
      }
    },
    [isGroup, setLuckyDialogIsOpen, setTransferDialogIsOpen, setTransferToIsOpen, t]
  );

  const { editor } = useEditorContext();

  const sendSticker = useCallback(
    (emojiId: string, groupId: string) => {
      sessionStore?.sendMessage(
        JSON.stringify({
          mediaType: 'lottie',
          id: emojiId,
          stickerSetInfo: {
            set_id: groupId,
            extra: 'hcs',
            category: StickerCategory.Sticker,
          },
        }),
        {
          payload_type: MsgPayloadType.STICKER,
        }
      );
    },
    [sessionStore]
  );

  useEffect(() => {
    if (sessionStore?.draftMessage?.replyMsgUser?.user_id) {
      if (canMention) {
        const isMeMsg = isMyMessage(
          refSessionStore.current.draftMessage!.replyMsg!,
          user?.user_id ?? ''
        );

        if (
          !isMeMsg &&
          refSessionStore.current.draftMessage!.replyMsgUser?.state !== StateEnum.DELETE_ACCOUNT
        ) {
          editor.insertMention(refSessionStore.current.draftMessage!.replyMsgUser!);
        }
      }
      editor.focus();
    }
  }, [editor, sessionStore?.draftMessage?.replyMsgUser?.user_id, user?.user_id, canMention]);

  useEffect(() => {
    const defaultDraftMessage = sessionStore?.draftMessage?.replyMsg?.payload;
    if (defaultDraftMessage && isEditing) {
      const res = serializePayload.deserializeValue(defaultDraftMessage);
      editor.setValue(res);
    }
    // when delete the reply message
    if (!sessionStore?.draftMessage?.replyId && isEditing) {
      editor.clear();
      refSessionStore.current?.setDraftMessage({ isEditing: false });
    }
  }, [
    editor,
    sessionStore?.draftMessage?.replyMsg,
    isEditing,
    sessionStore?.draftMessage?.replyId,
  ]);

  return (
    <MessageComposer
      headerExtra={
        sessionStore?.session && (
          <MsgReplyPreview
            session={sessionStore.session}
            onChange={setMsgReplyPreviewChangeEvent}
          />
        )
      }
      sendMessageDisabled={disabled}
      css={{
        '& *': { pointerEvents: loading ? 'none' : 'auto' },
        opacity: loading ? 0.6 : 1,
      }}
    >
      <MessageTextArea
        sendMessageDisabled={disabled}
        editChanged={editChanged}
        isEditing={isEditing}
        ref={refMessageTextArea}
        focusTrigger={focusTrigger}
        placeholder={
          loading
            ? t('chat.loadingText')
            : isGroup
              ? sessionStore?.session?.name
              : t('chat.messageSession', {
                  name:
                    sessionStore?.session?.user_info?.state === StateEnum.DELETE_ACCOUNT
                      ? t('chat.deleteAccountText')
                      : `${sessionStore?.session?.user_info?.name ?? ''}${sessionStore?.session?.user_info?.secondary_name && `(${sessionStore?.session?.user_info?.secondary_name})`}`,
                })
        }
        onSend={() => {
          playSendSound();
          sessionStore?.sendDraftMessage();
          onSend?.();
        }}
        onChange={({ payload, context }: SerializePayloadResult) => {
          sessionStore?.setDraftMessage({
            payload,
            mentionIds: context.mentionIds,
          });
        }}
        onValidChange={(valid: boolean) => {
          setValid(valid);
        }}
      />
      {!disabled && (
        <Flex>
          <ToolBar
            isMe={isMe}
            isGroup={isGroup}
            readonly={readonly}
            duration={sessionStore?.session?.expire_duration}
            onDurationChange={onDurationChange}
            onClickImage={() => {
              pick({
                accept: getAccept(PickFileDialogSource.IMAGE),
                multiple: true,
                onFiles: (files) => {
                  onPickFile?.(PickFileDialogSource.IMAGE, files);
                },
              });
            }}
            onClickFile={() => {
              pick({
                accept: getAccept(PickFileDialogSource.FILE),
                multiple: true,
                onFiles: (files) => {
                  onPickFile?.(PickFileDialogSource.FILE, files);
                },
              });
            }}
            onLuckyChange={handleLuckyChange}
            onSelectSticker={sendSticker}
          />
          <SendButton
            active={valid && editChanged}
            isEditing={isEditing}
            onClick={() => {
              refMessageTextArea.current?.send();
            }}
          />
        </Flex>
      )}
    </MessageComposer>
  );
}
